import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, LogOut, Upload, ChevronUp, ChevronDown } from "lucide-react";
import api, { inr } from "@/api";
import { compressImage } from "@/utils/image";
import Logo from "@/components/Logo";
import { SiteTextTab } from "@/settings";
import { PagesTab } from "@/pages/admin/PagesAdmin";

const CONFIGS = {
  projects: {
    label: "Projects",
    fields: [
      { name: "title", label: "Title" },
      { name: "category", label: "Category", type: "select", options: ["Residential", "Architecture", "Interiors", "Sustainable Architecture", "Research & Material Exploration", "Visualisation"] },
      { name: "location", label: "Location" },
      { name: "year", label: "Year", type: "number" },
      { name: "status", label: "Status" },
      { name: "type", label: "Project Type" },
      { name: "area", label: "Area" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "concept", label: "Concept", type: "textarea" },
      { name: "materials", label: "Materials (comma separated)", type: "list" },
      { name: "sustainability", label: "Sustainability Strategy", type: "textarea" },
      { name: "cover", label: "Cover Image URL" },
      { name: "drawings", label: "Drawings & Sketches", type: "imagelist" },
      { name: "gallery", label: "Gallery", type: "imagelist" },
      { name: "featured", label: "Featured on homepage", type: "checkbox" },
    ],
  },
  artworks: {
    label: "Artworks",
    fields: [
      { name: "title", label: "Title" },
      { name: "category", label: "Category", type: "select", options: ["Portraits", "Paintings", "Wall Art", "Illustration", "Commissioned Art", "Experimental Art"] },
      { name: "medium", label: "Medium" },
      { name: "size", label: "Size" },
      { name: "year", label: "Year", type: "number" },
      { name: "price", label: "Price (₹, 0 = not priced)", type: "number" },
      { name: "for_sale", label: "For sale", type: "checkbox" },
      { name: "featured", label: "Featured on homepage", type: "checkbox" },
      { name: "image", label: "Image URL" },
      { name: "description", label: "Description", type: "textarea" },
    ],
  },
  workshops: {
    label: "Workshops",
    fields: [
      { name: "title", label: "Title" },
      { name: "status", label: "Status", type: "select", options: ["upcoming", "past"] },
      { name: "featured", label: "Featured on homepage", type: "checkbox" },
      { name: "cover", label: "Cover Image URL" },
      { name: "dates", label: "Dates (display)" },
      { name: "start_date", label: "Start date (YYYY-MM-DD)" },
      { name: "duration", label: "Duration" },
      { name: "location", label: "Location" },
      { name: "mentors", label: "Mentors" },
      { name: "intro", label: "Introduction", type: "textarea" },
      { name: "content", label: "Content (comma separated)", type: "list" },
      { name: "schedule", label: "Schedule (comma separated)", type: "list" },
      { name: "eligibility", label: "Eligibility" },
      { name: "participants", label: "Max participants", type: "number" },
      { name: "fee", label: "Fee (₹, 0 = free)", type: "number" },
      { name: "payment_link", label: "Razorpay Payment Link (from your Razorpay dashboard)" },
      { name: "inclusions", label: "Inclusions (comma separated)", type: "list" },
      { name: "exclusions", label: "Exclusions (comma separated)", type: "list" },
      { name: "accommodation", label: "Accommodation info" },
      { name: "gallery", label: "Gallery URLs (comma separated)", type: "list" },
      { name: "terms", label: "Terms (comma separated)", type: "list" },
      { name: "faqs", label: "FAQs — one per line: Question | Answer", type: "faq" },
    ],
  },
  products: {
    label: "Products",
    fields: [
      { name: "name", label: "Name" },
      { name: "category", label: "Category" },
      { name: "price", label: "Price (₹)", type: "number" },
      { name: "payment_link", label: "Razorpay Payment Link (from your Razorpay dashboard)" },
      { name: "quantity", label: "Quantity available", type: "number" },
      { name: "status", label: "Status", type: "select", options: ["Available", "Sold Out", "Limited Edition"] },
      { name: "featured", label: "Featured on homepage", type: "checkbox" },
      { name: "dimensions", label: "Dimensions" },
      { name: "material", label: "Material" },
      { name: "images", label: "Image URLs (comma separated)", type: "list" },
      { name: "description", label: "Description", type: "textarea" },
    ],
  },
  team: {
    label: "Team",
    fields: [
      { name: "name", label: "Name" },
      { name: "kind", label: "Type", type: "select", options: ["Founder", "Core Team", "Collaborator"] },
      { name: "role", label: "Role (e.g. Founder — Architect & Artist)" },
      { name: "bio", label: "Short biography / introduction", type: "textarea" },
      { name: "photo", label: "Portrait photo URL" },
      { name: "order", label: "Display order (number)", type: "number" },
    ],
  },
};

function toForm(item, fields) {
  const out = {};
  fields.forEach((f) => {
    const v = item?.[f.name];
    if (f.type === "list") out[f.name] = Array.isArray(v) ? v.join(", ") : "";
    else if (f.type === "imagelist")
      out[f.name] = Array.isArray(v)
        ? v.map((x) => (typeof x === "string" ? { url: x, caption: "", alt: "" } : { url: x.url || "", caption: x.caption || "", alt: x.alt || "" })).filter((x) => x.url)
        : [];
    else if (f.type === "faq") out[f.name] = Array.isArray(v) ? v.map((x) => `${x.q} | ${x.a}`).join("\n") : "";
    else if (f.type === "checkbox") out[f.name] = !!v;
    else out[f.name] = v ?? "";
  });
  return out;
}

function fromForm(form, fields) {
  const out = {};
  fields.forEach((f) => {
    const v = form[f.name];
    if (f.type === "list") out[f.name] = v.split(",").map((s) => s.trim()).filter(Boolean);
    else if (f.type === "imagelist") out[f.name] = (Array.isArray(v) ? v : []).filter((x) => x && x.url);
    else if (f.type === "faq") out[f.name] = v.split("\n").map((l) => l.split("|").map((s) => s.trim())).filter((p) => p.length === 2 && p[0]).map(([q, a]) => ({ q, a }));
    else if (f.type === "number") out[f.name] = Number(v) || 0;
    else if (f.type === "checkbox") out[f.name] = !!v;
    else out[f.name] = v;
  });
  return out;
}

function Editor({ collection, item, onClose, onSaved }) {
  const cfg = CONFIGS[collection];
  const [form, setForm] = useState(() => toForm(item, cfg.fields));
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const uploadTarget = useRef(null);
  const [uploading, setUploading] = useState(null);

  const pickFile = (f) => {
    uploadTarget.current = f;
    fileRef.current.click();
  };

  const onFile = async (e) => {
    const rawFile = e.target.files?.[0];
    const f = uploadTarget.current;
    e.target.value = "";
    if (!rawFile || !f) return;
    setUploading(f.name);
    try {
      const file = await compressImage(rawFile);
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.url;
      setForm((s) => {
        if (f.type === "list") return { ...s, [f.name]: s[f.name] ? `${s[f.name]}, ${url}` : url };
        if (f.type === "imagelist") return { ...s, [f.name]: [...(s[f.name] || []), { url, caption: "", alt: "" }] };
        return { ...s, [f.name]: url };
      });
      toast.success("File uploaded — don't forget to Save.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = fromForm(form, cfg.fields);
    try {
      if (item?.id) await api.put(`/admin/${collection}/${item.id}`, payload);
      else await api.post(`/admin/${collection}`, payload);
      toast.success("Saved.");
      onSaved();
    } catch {
      toast.error("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-paper border border-ink/20 focus:border-brand outline-none px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="admin-editor">
      <div className="bg-paper w-full max-w-2xl p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display text-2xl">{item?.id ? "Edit" : "New"} {cfg.label.slice(0, -1)}</h2>
          <button onClick={onClose} data-testid="editor-close" aria-label="Close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="space-y-5">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
            data-testid="editor-file-input" onChange={onFile} />
          {cfg.fields.map((f) => (
            <div key={f.name}>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 mb-1.5">{f.label}</label>
              {f.type === "imagelist" ? (
                <ImageListEditor
                  value={form[f.name] || []}
                  onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))}
                  onUpload={() => pickFile(f)}
                  uploading={uploading === f.name}
                  testid={`imagelist-${f.name}`}
                />
              ) : f.type === "textarea" || f.type === "faq" ? (
                <textarea rows={f.type === "faq" ? 4 : 3} value={form[f.name]} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} className={`${inputCls} resize-y`} data-testid={`field-${f.name}`} />
              ) : f.type === "select" ? (
                <select value={form[f.name]} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} className={inputCls} data-testid={`field-${f.name}`}>
                  <option value="">—</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <input type="checkbox" checked={form[f.name]} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.checked }))} className="w-4 h-4 accent-[#E53827]" data-testid={`field-${f.name}`} />
              ) : (
                <div className="flex gap-2">
                  <input type={f.type === "number" ? "number" : "text"} value={form[f.name]} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} className={inputCls} data-testid={`field-${f.name}`} />
                  {["cover", "image", "photo", "images", "gallery", "drawings"].includes(f.name) && (
                    <button type="button" onClick={() => pickFile(f)} disabled={!!uploading} data-testid={`upload-${f.name}`}
                      className="shrink-0 inline-flex items-center gap-2 border border-ink/25 px-4 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors disabled:opacity-50">
                      {uploading === f.name ? "Uploading…" : <><Upload size={12} /> Upload</>}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          <button type="submit" disabled={busy} data-testid="editor-save-btn"
            className="bg-ink text-paper px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function CollectionTab({ collection }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | item

  const load = () => api.get(`/${collection}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [collection]);

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${collection}/${id}`);
    toast.success("Deleted.");
    load();
  };

  const titleOf = (it) => it.title || it.name;
  const imgOf = (it) => it.cover || it.image || it.images?.[0];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink/50">{items.length} items</p>
        <button onClick={() => setEditing({})} data-testid={`add-${collection}-btn`}
          className="inline-flex items-center gap-2 bg-ink text-paper px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`admin-item-${it.id}`} className="flex items-center gap-4 border border-ink/15 bg-paper p-3">
            {imgOf(it) && <img src={imgOf(it)} alt="" className="w-14 h-14 object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg truncate">{titleOf(it)}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40">
                {it.category || it.status || ""}{it.price != null && it.price > 0 ? ` · ${inr(it.price)}` : ""}{it.fee ? ` · ${inr(it.fee)}` : ""}
              </p>
            </div>
            <button onClick={() => setEditing(it)} data-testid={`edit-${it.id}`} className="p-2 hover:text-brand transition-colors"><Pencil size={16} /></button>
            <button onClick={() => del(it.id)} data-testid={`delete-${it.id}`} className="p-2 hover:text-brand transition-colors"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing !== null && (
        <Editor collection={collection} item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function DataTable({ collection, columns, testid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/admin/${collection}`).then((r) => setRows(r.data)).catch(() => {}); }, [collection]);
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ink/20">
            {columns.map((c) => <th key={c.key} className="text-left py-3 pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-ink/10 align-top">
              {columns.map((c) => (
                <td key={c.key} className="py-3 pr-4 text-ink/80">{c.render ? c.render(r) : String(r[c.key] ?? "—")}</td>
              ))}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={columns.length} className="py-8 text-ink/40 font-mono text-xs">Nothing here yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function CommunityTab() {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/admin/community${filter === "all" ? "" : `?type=${filter}`}`).then((r) => setRows(r.data)).catch(() => {});
  }, [filter]);
  const filters = [{ key: "all", label: "All" }, { key: "art", label: "Art" }, { key: "architecture", label: "Architecture" }];
  const columns = [
    { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("en-IN") },
    { key: "community_type", label: "Community", render: (r) => (r.community_type === "art" ? "Art" : "Architecture") },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "whatsapp", label: "WhatsApp", render: (r) => r.whatsapp || "—" },
    { key: "city", label: "City", render: (r) => r.city || "—" },
    { key: "status", label: "Status", render: (r) => (
      <span>{r.status}{r.status === "student" && (r.course || r.study_year) ? ` · ${[r.course, r.study_year].filter(Boolean).join(", ")}` : ""}</span>
    ) },
    { key: "details", label: "Details", render: (r) => (
      <span className="line-clamp-3 max-w-[220px] inline-block">
        {r.community_type === "art"
          ? r.mediums || "—"
          : [r.services_offered, (r.software || []).map((s) => `${s.name} ${s.proficiency}%`).join(", "), r.other_software].filter(Boolean).join(" · ") || "—"}
        {r.experience ? ` (${r.experience})` : ""}
      </span>
    ) },
    { key: "portfolio_path", label: "Portfolio", render: (r) => (
      <span className="inline-flex gap-4">
        {r.portfolio_path && (
          <a data-testid={`community-portfolio-${r.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/community/file/${r.portfolio_path}`} target="_blank" rel="noreferrer" className="text-brand underline">File</a>
        )}
        {r.portfolio_link && (
          <a data-testid={`community-link-${r.id}`} href={r.portfolio_link} target="_blank" rel="noreferrer" className="text-brand underline">Link</a>
        )}
        {!r.portfolio_path && !r.portfolio_link && "—"}
      </span>
    ) },
  ];
  return (
    <div data-testid="admin-community">
      <div className="flex flex-wrap gap-2 mb-6" data-testid="community-filters">
        {filters.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`community-filter-${f.key}`}
            className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
              filter === f.key ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/60 hover:border-brand hover:text-brand"
            }`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink/20">
              {columns.map((c) => <th key={c.key} className="text-left py-3 pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-ink/10 align-top" data-testid={`community-row-${r.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="py-3 pr-4 text-ink/80">{c.render ? c.render(r) : String(r[c.key] ?? "—")}</td>
                ))}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={columns.length} className="py-8 text-ink/40 font-mono text-xs">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "settings", label: "Site Text" },
  { key: "pages", label: "Pages" },
  { key: "projects", label: "Projects" },
  { key: "artworks", label: "Artworks" },
  { key: "workshops", label: "Workshops" },
  { key: "products", label: "Products" },
  { key: "team", label: "Team" },
  { key: "enquiries", label: "Enquiries" },
  { key: "applications", label: "Applicants" },
  { key: "community", label: "Community" },
  { key: "registrations", label: "Registrations" },
  { key: "orders", label: "Orders" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("iksha_admin_token")) {
      navigate("/admin");
      return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("iksha_admin_token");
      navigate("/admin");
    });
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [navigate]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("iksha_admin_token");
    navigate("/admin");
  };

  const content = useMemo(() => {
    if (tab === "overview") {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
          {stats && Object.entries(stats).map(([k, v]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`stat-${k}`}
              className="border border-ink/15 bg-paper p-6 text-left hover:border-brand transition-colors">
              <p className="font-display text-4xl">{v}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 mt-2">{k}</p>
            </button>
          ))}
        </div>
      );
    }
    if (tab === "settings") return <SiteTextTab />;
    if (tab === "pages") return <PagesTab />;
    if (tab === "applications") {
      return <DataTable collection="applications" testid="admin-applications" columns={[
        { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("en-IN") },
        { key: "name", label: "Name" }, { key: "email", label: "Email" },
        { key: "position", label: "Position" },
        { key: "software", label: "Software", render: (r) => <span className="line-clamp-2 max-w-[200px] inline-block">{r.software || "—"}</span> },
        { key: "portfolio_path", label: "Files", render: (r) => (
          <span className="inline-flex gap-4">
            <a data-testid={`portfolio-${r.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/careers/file/${r.portfolio_path}`} target="_blank" rel="noreferrer" className="text-brand underline">Portfolio</a>
            <a data-testid={`cv-${r.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/careers/file/${r.cv_path}`} target="_blank" rel="noreferrer" className="text-brand underline">CV</a>
          </span>
        ) },
      ]} />;
    }
    if (CONFIGS[tab]) return <CollectionTab collection={tab} />;
    if (tab === "community") return <CommunityTab />;
    if (tab === "enquiries") {
      return <DataTable collection="enquiries" testid="admin-enquiries" columns={[
        { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("en-IN") },
        { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
        { key: "type", label: "Type" }, { key: "message", label: "Message", render: (r) => <span className="line-clamp-2 max-w-xs inline-block">{r.message}</span> },
      ]} />;
    }
    if (tab === "registrations") {
      return <DataTable collection="registrations" testid="admin-registrations" columns={[
        { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("en-IN") },
        { key: "workshop_title", label: "Workshop" }, { key: "name", label: "Name" }, { key: "email", label: "Email" },
        { key: "phone", label: "Phone" }, { key: "city", label: "City" },
        { key: "fee", label: "Fee", render: (r) => inr(r.fee) },
        { key: "payment_status", label: "Payment", render: (r) => <span className={r.payment_status === "paid" ? "text-green-700" : "text-brand"}>{r.payment_status}</span> },
      ]} />;
    }
    if (tab === "orders") {
      return <DataTable collection="orders" testid="admin-orders" columns={[
        { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("en-IN") },
        { key: "customer", label: "Customer", render: (r) => `${r.customer?.name} · ${r.customer?.email}` },
        { key: "items", label: "Items", render: (r) => r.items.map((i) => `${i.name} ×${i.quantity}`).join(", ") },
        { key: "total", label: "Total", render: (r) => inr(r.total) },
        { key: "status", label: "Status" },
      ]} />;
    }
    return null;
  }, [tab, stats]);

  if (!user) return <main className="min-h-screen bg-linen flex items-center justify-center"><p className="font-mono text-sm text-ink/40">Loading…</p></main>;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-linen">
      <header className="bg-ink text-paper px-6 md:px-10 py-4 flex items-center justify-between">
        <Logo dark small />
        <div className="flex items-center gap-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 hidden sm:inline">{user.email}</span>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:text-brand transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>
      <div className="px-6 md:px-10 py-8 max-w-6xl mx-auto">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                tab === t.key ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/60 hover:border-brand hover:text-brand"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
        {content}
      </div>
    </main>
  );
}

function ImageListEditor({ value, onChange, onUpload, uploading, testid }) {
  const items = value || [];
  const inputCls = "w-full bg-paper border border-ink/20 focus:border-brand outline-none px-3 py-2 text-sm";
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) => {
    const a = [...items];
    const [x] = a.splice(i, 1);
    a.splice(Math.max(0, Math.min(a.length, i + dir)), 0, x);
    onChange(a);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { url: "", caption: "", alt: "" }]);

  return (
    <div data-testid={testid} className="space-y-4">
      {items.map((it, i) => (
        <div key={i} className="flex gap-3 border border-ink/15 bg-linen p-3" data-testid={`${testid}-item-${i}`}>
          {it.url ? (
            <img src={it.url} alt={it.alt || it.caption || `Image ${i + 1}`} className="w-16 h-16 object-cover shrink-0 border border-ink/10" />
          ) : (
            <div className="w-16 h-16 shrink-0 border border-dashed border-ink/25 flex items-center justify-center font-mono text-[9px] text-ink/40 text-center leading-tight">
              no image
            </div>
          )}
          <div className="flex-1 space-y-2 min-w-0">
            <input value={it.url} onChange={(e) => update(i, "url", e.target.value)} placeholder="Image URL" className={inputCls} data-testid={`${testid}-url-${i}`} />
            <input value={it.caption} onChange={(e) => update(i, "caption", e.target.value)} placeholder="Caption (optional — shown under the image and in the zoom viewer)" className={inputCls} data-testid={`${testid}-caption-${i}`} />
            <input value={it.alt} onChange={(e) => update(i, "alt", e.target.value)} placeholder="Alt text (accessibility)" className={inputCls} data-testid={`${testid}-alt-${i}`} />
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move image up" className="p-1.5 hover:text-brand disabled:opacity-30 transition-colors"><ChevronUp size={14} /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move image down" className="p-1.5 hover:text-brand disabled:opacity-30 transition-colors"><ChevronDown size={14} /></button>
            <button type="button" onClick={() => remove(i)} aria-label="Remove image" className="p-1.5 hover:text-brand transition-colors"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onUpload} disabled={uploading} data-testid={`${testid}-upload`}
          className="inline-flex items-center gap-2 border border-ink/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors disabled:opacity-50">
          {uploading ? "Uploading…" : <><Upload size={12} /> Upload image</>}
        </button>
        <button type="button" onClick={add} data-testid={`${testid}-add-url`}
          className="inline-flex items-center gap-2 border border-ink/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-brand hover:text-brand transition-colors">
          <Plus size={12} /> Add by URL
        </button>
      </div>
    </div>
  );
}
